import React from 'react'
import * as yup from 'yup'
import { FormProvider } from 'react-hook-form'
import { useIntl } from 'react-intl'

import useForm from '../../hooks/useForm'
import TextField from '../formControls/TextField'
import FormRow from '../formControls/FormRow'
import { getNameExtensionFromFileName } from '../../services/helpers'
import SaveCancelButtonGroup from '../Button/SaveCancelButtonGroup'

export type ModalEditFileNameProps = {
	file: File
	onSubmit: (file: File, fileName: string) => void
	visible: boolean
	onClose: () => void
}

const ModalEditFileName = ({
	visible = false,
	onSubmit,
	file,
	onClose,
}: ModalEditFileNameProps) => {
	const { formatMessage } = useIntl()
	const schema = yup.object().shape({
		fileName: yup
			.string()
			.required(
				formatMessage({ id: 'validation.file_name.required' })
			)
			.test(
				'periods',
				formatMessage({ id: 'validation.file_name.no_periods' }),
				(val) => !/\./.test(val)
			),
	})

	const { name, extension } = getNameExtensionFromFileName(file.name)

	const methods = useForm({
		defaultValues: {
			fileName: name,
		},
		schema,
		values: {
			fileName: name,
		},
	})

	const handleSubmit = (data: { fileName: string }) => {
		const newFileName = `${data.fileName}.${extension}`
		onSubmit(file, newFileName)
		onClose()
	}

	const handleSaveClick = () => {
		methods.handleSubmit(handleSubmit)()
	}

	const handleCloseClick = () => {
		onClose()
	}

	if (!visible) return null

	return (
		<div className="absolute top-0 left-0 w-full h-full flex items-center z-10 bg-white">
			<FormProvider {...methods}>
				<form className="flex gap-4 items-end w-full">
					<FormRow className="flex gap-2 items-end flex-1">
						<TextField
							label={formatMessage({ id: 'global.file_name' })}
							name="fileName"
							className="w-full"
						/>
						<span>.{extension}</span>
					</FormRow>
					<FormRow>
						<SaveCancelButtonGroup
							onSaveClick={handleSaveClick}
							onCancelClick={handleCloseClick}
						/>
					</FormRow>
				</form>
			</FormProvider>
		</div>
	)
}

export default ModalEditFileName
